// AchatsPage.js — Module Achats & Décaissements — SOGAS V2 (design refonte)
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

// ─── Palette SOGAS officielle ─────────────────────────────────────────────────
const C = {
  primary:   '#2E7D32',
  primaryDark:'#1B5E20',
  primaryLight:'#43A047',
  success:   '#2E7D32',
  warning:   '#F57F17',
  danger:    '#B71C1C',
  orange:    '#E65100',
  purple:    '#4527A0',
  gray:      '#616161',
  grayLight: '#9E9E9E',
  light:     '#F4F6F9',
  green50:   '#F1F8F2',
  green100:  '#E8F5E9',
  white:     '#FFFFFF',
  border:    '#E0E0E0',
  textDark:  '#212121',
  textMid:   '#616161',
  amber:     '#F57F17',
  amberBg:   '#FFF3E0',
};

// Statuts avec couleurs pill officielles
const STATUTS_CONFIG = {
  BROUILLON:       { label: 'Brouillon',        bg: '#F5F5F5',  color: '#757575' },
  SOUMIS:          { label: 'Soumis',           bg: '#E3F2FD',  color: '#1565C0' },
  EN_MAINTENANCE:  { label: 'Maintenance',       bg: '#F3E5F5',  color: '#6A1B9A' },
  EN_ACHATS:       { label: 'En Achats',         bg: '#EDE7F6',  color: '#4527A0' },
  EN_CONTROLE:     { label: 'Contrôle',          bg: '#FCE4EC',  color: '#880E4F' },
  EN_COMPTABILITE: { label: 'Comptabilité',      bg: '#FFF3E0',  color: '#BF360C' },
  APPROUVE:        { label: 'Approuvé',          bg: '#E8F5E9',  color: '#2E7D32' },
  EN_EXECUTION:    { label: 'En Exécution',      bg: '#E0F2F1',  color: '#004D40' },
  LIVRE:           { label: 'Livré',             bg: '#F1F8E9',  color: '#33691E' },
  FACTURE_RECUE:   { label: 'Facture Reçue',     bg: '#FFF8E1',  color: '#E65100' },
  PAYE:            { label: '✓ Payé',            bg: '#E8F5E9',  color: '#1B5E20' },
  CLOTURE:         { label: 'Clôturé',           bg: '#ECEFF1',  color: '#546E7A' },
  REJETE:          { label: 'Rejeté',            bg: '#FFEBEE',  color: '#B71C1C' },
};

const Badge = ({ statut }) => {
  const cfg = STATUTS_CONFIG[statut] || { label: statut, bg: '#F5F5F5', color: '#757575' };
  return (
    <span style={{
      background: cfg.bg,
      color: cfg.color,
      padding: '3px 10px',
      borderRadius: 20,
      fontSize: 11,
      fontWeight: 700,
      whiteSpace: 'nowrap',
      letterSpacing: 0.2,
    }}>{cfg.label}</span>
  );
};

const formatMontant = (n) =>
  n ? Number(n).toLocaleString('fr-FR', { minimumFractionDigits: 0 }) + ' FCFA' : '—';

const StatCard = ({ label, value, color, sub, icon }) => (
  <div style={{
    background: C.white,
    borderRadius: 12,
    padding: '16px 20px',
    border: `1px solid ${C.border}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    flex: 1,
    minWidth: 150,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      {icon && (
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          background: color + '18',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, flexShrink: 0,
        }}>{icon}</div>
      )}
      <div style={{ fontSize: 12, color: C.textMid, fontWeight: 500 }}>{label}</div>
    </div>
    <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 11, color: C.grayLight }}>{sub}</div>}
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
export default function AchatsPage() {
  const { utilisateur: moi, logout } = useAuth();
  const navigate = useNavigate();
  const monRole = moi?.role || '';
  const ROLES_FINANCIERS = ['admin', 'super_admin', 'service_achats', 'responsable_achats',
    'verificateur', 'controle_gestion', 'comptable', 'caissier', 'caissier_principal',
    'direction', 'responsable_preposes', 'comptabilite'];
  const voitMontants = ROLES_FINANCIERS.includes(monRole);
  const peutCreer = ['super_admin','admin','service_achats','prepose','responsable_preposes'].includes(monRole);
  const peutGererDossier = ['super_admin','admin','service_achats'].includes(monRole);
  const peutVoirFournisseurs = ['responsable_achats','admin','super_admin'].includes(monRole);
  const [onglet, setOnglet] = useState('dossiers'); // dossiers | fournisseurs | seuils
  const [dossiers, setDossiers] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [stats, setStats] = useState(null);
  const [abattoirs, setAbattoirs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState('');

  // Filtres dossiers
  const [filtreStatut, setFiltreStatut] = useState('');
  const [filtreType, setFiltreType] = useState('');
  const [filtreAbattoir, setFiltreAbattoir] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});

  // Modals
  const [showNouveauDossier, setShowNouveauDossier] = useState(false);
  const [servicesRH, setServicesRH] = useState([]);
  const [showDetailDossier, setShowDetailDossier] = useState(null);
  const [showNouveauFournisseur, setShowNouveauFournisseur] = useState(false);

  const chargerDonnees = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 15 });
      if (filtreStatut) params.append('statut', filtreStatut);
      if (filtreType) params.append('type_dossier', filtreType);
      if (filtreAbattoir) params.append('abattoir_id', filtreAbattoir);

      const [dossiersRes, statsRes, abattoirsRes] = await Promise.all([
        api.get(`/achats/dossiers?${params}`).catch(() => ({ data: { dossiers: [], pagination: {} } })),
        api.get('/achats/stats').catch(() => ({ data: null })),
        api.get('/abattoirs').catch(() => ({ data: [] })),
      ]);
      setDossiers(dossiersRes.data.dossiers || []);
      setPagination(dossiersRes.data.pagination || {});
      setStats(statsRes.data);
      setAbattoirs(abattoirsRes.data || []);
      setErreur('');
    } catch (e) {
      setErreur('Erreur chargement données');
    } finally {
      setLoading(false);
    }
  }, [page, filtreStatut, filtreType, filtreAbattoir]);

  const chargerFournisseurs = useCallback(async () => {
    try {
      const res = await api.get('/achats/fournisseurs');
      setFournisseurs(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { chargerDonnees(); }, [chargerDonnees]);
  useEffect(() => { if (onglet === 'fournisseurs') chargerFournisseurs(); }, [onglet, chargerFournisseurs]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: '100vh', background: C.light, fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}>

      {/* ── TOPBAR ─────────────────────────────────────────────────────────── */}
      <div style={{
        height: 52, background: C.white, borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 24px', position: 'sticky', top: 0, zIndex: 50,
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      }}>
        {/* Breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: C.grayLight }}>
          <span style={{ cursor: 'pointer', color: C.primary, fontWeight: 600 }}
            onClick={() => navigate('/achats')}>Achats</span>
          <span>›</span>
          <span style={{ color: C.textDark, fontWeight: 500 }}>Dossiers</span>
        </div>
        {/* Search */}
        <div style={{ position: 'relative', width: 280 }}>
          <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: C.grayLight, fontSize: 14, pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            placeholder="Rechercher dans Dossiers..."
            value={filtreStatut === '' && filtreType === '' ? '' : ''}
            readOnly
            style={{
              width: '100%', height: 34, paddingLeft: 32, paddingRight: 12,
              border: `1.5px solid ${C.border}`, borderRadius: 8,
              background: '#F5F5F5', fontSize: 13, color: C.textMid,
              outline: 'none', boxSizing: 'border-box', cursor: 'default',
            }}
          />
        </div>
        {/* Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={() => setShowNouveauDossier(true)} style={{
            background: C.primary, color: '#fff', border: 'none',
            padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
            fontSize: 13, fontWeight: 700, transition: '0.15s ease',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>+ Nouveau</button>
        </div>
      </div>

      <div style={{ padding: '20px 24px' }}>

      {erreur && (
        <div style={{ background: '#FFEBEE', border: `1px solid #EF9A9A`, color: C.danger,
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
          {erreur}
        </div>
      )}

      {/* ── KPI CARDS ──────────────────────────────────────────────────────── */}
      {stats && (
        <div style={{ display: 'flex', gap: 14, marginBottom: 20, flexWrap: 'wrap' }}>
          <StatCard
            icon="⏳" label="En attente d'action"
            value={stats.en_attente ?? 0} color="#F57F17" sub="dossiers actifs"
          />
          {voitMontants && (
            <StatCard icon="📈" label="Engagé ce mois"
              value={formatMontant(stats.montants?.total_estime)} color="#1565C0" />
          )}
          {voitMontants && (
            <StatCard icon="✅" label="Approuvé"
              value={formatMontant(stats.montants?.total_approuve)} color={C.primary} />
          )}
          {voitMontants && (
            <StatCard icon="💰" label="Décaissé"
              value={formatMontant(stats.montants?.total_paye)} color={C.primaryDark} />
          )}
        </div>
      )}

      {/* ── ONGLETS (style Odoo) ────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1.5px solid ${C.border}`, marginBottom: 18 }}>
        {[
          { id: 'dossiers', label: '📁 Dossiers' },
          ...(peutVoirFournisseurs ? [{ id: 'fournisseurs', label: '🏢 Fournisseurs' }] : []),
          { id: 'seuils', label: '⚙️ Seuils' },
        ].map(t => (
          <button key={t.id} onClick={() => setOnglet(t.id)} style={{
            border: 'none', background: 'none', padding: '10px 22px',
            cursor: 'pointer', fontSize: 13,
            fontWeight: onglet === t.id ? 700 : 500,
            color: onglet === t.id ? C.primary : C.gray,
            borderBottom: onglet === t.id ? `2px solid ${C.primary}` : '2px solid transparent',
            marginBottom: -1.5, transition: '0.15s ease',
          }}>{t.label}</button>
        ))}
      </div>

      {/* ── ONGLET DOSSIERS ───────────────────────────────────────────────── */}
      {onglet === 'dossiers' && (
        <div>
          {/* ── Barre de filtres ── */}
          <div style={{
            display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap',
            background: C.white, padding: '12px 16px',
            borderRadius: 10, border: `1px solid ${C.border}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
            alignItems: 'center',
          }}>
            <select value={filtreStatut} onChange={e => { setFiltreStatut(e.target.value); setPage(1); }}
              style={selectStyle}>
              <option value="">Tous les statuts</option>
              {Object.entries(STATUTS_CONFIG).map(([k, v]) =>
                <option key={k} value={k}>{v.label}</option>
              )}
            </select>
            <select value={filtreType} onChange={e => { setFiltreType(e.target.value); setPage(1); }}
              style={selectStyle}>
              <option value="">Tous les types</option>
              <option value="ACHAT">Achat</option>
              <option value="TRAVAUX">Travaux</option>
            </select>
            <select value={filtreAbattoir} onChange={e => { setFiltreAbattoir(e.target.value); setPage(1); }}
              style={selectStyle}>
              <option value="">Tous les abattoirs</option>
              {abattoirs.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
            </select>
            {(filtreStatut || filtreType || filtreAbattoir) && (
              <button onClick={() => { setFiltreStatut(''); setFiltreType(''); setFiltreAbattoir(''); setPage(1); }}
                style={{
                  background: '#FFEBEE', color: C.danger, border: `1px solid #FFCDD2`,
                  padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                }}>✕ Réinitialiser</button>
            )}
            <div style={{ marginLeft: 'auto', fontSize: 12, color: C.grayLight }}>
              {pagination.total ? `${pagination.total} dossier${pagination.total > 1 ? 's' : ''}` : ''}
            </div>
          </div>

          {/* ── Table dossiers ── */}
          {loading ? (
            <div style={{
              textAlign: 'center', padding: 60, color: C.grayLight,
              background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
            }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
              <div style={{ fontSize: 14 }}>Chargement des dossiers…</div>
            </div>
          ) : dossiers.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: 60, color: C.grayLight,
              background: C.white, borderRadius: 12, border: `1px solid ${C.border}`,
            }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📭</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: C.textMid }}>Aucun dossier trouvé</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Modifiez les filtres ou créez un nouveau dossier</div>
            </div>
          ) : (
            <div style={{
              background: C.white, borderRadius: 12,
              border: `1px solid ${C.border}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: '#F8F9FA', borderBottom: `2px solid ${C.border}` }}>
                    {['N° Dossier', 'Type', 'Objet', 'Abattoir', 'Demandeur', 'Montant', 'Proformats', 'Statut', ''].map(h => (
                      <th key={h} style={{
                        padding: '10px 14px', textAlign: 'left',
                        fontSize: 11, fontWeight: 700, color: C.textMid,
                        textTransform: 'uppercase', letterSpacing: '0.5px',
                        whiteSpace: 'nowrap',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {dossiers.map((d) => (
                    <tr key={d.id}
                      onClick={() => navigate(`/achats/dossiers/${d.numero}`)}
                      style={{
                        borderBottom: `1px solid #F0F2F4`,
                        cursor: 'pointer', transition: 'background 0.1s ease',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = C.green50}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 700, color: C.primary, fontSize: 12, fontFamily: 'monospace' }}>
                          {d.numero}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{
                          background: d.type_dossier === 'TRAVAUX' ? '#FFF3E0' : '#E3F2FD',
                          color: d.type_dossier === 'TRAVAUX' ? C.orange : '#1565C0',
                          padding: '2px 9px', borderRadius: 20, fontSize: 10, fontWeight: 700,
                        }}>{d.type_dossier}</span>
                      </td>
                      <td style={{ ...tdStyle, maxWidth: 220 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: C.textDark }}
                          title={d.objet}>{d.objet}</div>
                        {d.urgence && (
                          <span style={{ fontSize: 10, color: C.amber, fontWeight: 700, display: 'block', marginTop: 1 }}>⚡ URGENT</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, color: C.textMid, fontSize: 12 }}>{d.abattoir_nom || '—'}</td>
                      <td style={{ ...tdStyle, color: C.textMid, fontSize: 12 }}>{d.demandeur_nom || '—'}</td>
                      <td style={{ ...tdStyle, fontWeight: 700, textAlign: 'right' }}>
                        {voitMontants
                          ? <span style={{ color: C.primaryDark }}>{formatMontant(d.montant_estime)}</span>
                          : <span style={{ fontSize: 11, color: C.grayLight }}>🔒</span>}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {/* Dots proformats */}
                        <span style={{ letterSpacing: 2, fontSize: 12 }}>
                          {[0,1,2].map(i => (
                            <span key={i} style={{ color: i < (d.nb_proformats || 0) ? C.primary : '#E0E0E0' }}>●</span>
                          ))}
                        </span>
                        <span style={{ fontSize: 11, color: C.grayLight, marginLeft: 4 }}>
                          {d.nb_proformats || 0}/3
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <Badge statut={d.statut} />
                        {d.retourne_par_controle && d.statut === 'EN_ACHATS' && (
                          <span style={{
                            background: '#FFEBEE', color: '#B71C1C',
                            fontSize: 10, padding: '2px 7px', borderRadius: 20,
                            marginLeft: 6, fontWeight: 700, display: 'inline-block',
                          }}>🔄 Retourné</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <button
                          onClick={e => { e.stopPropagation(); navigate(`/achats/dossiers/${d.numero}`); }}
                          style={{
                            background: C.green100, color: C.primary, border: `1px solid #A5D6A7`,
                            padding: '5px 14px', borderRadius: 8, cursor: 'pointer',
                            fontSize: 12, fontWeight: 600, transition: '0.15s ease',
                          }}>
                          Ouvrir →
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* ── Pagination ── */}
              {pagination.pages > 1 && (
                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: '12px 16px', borderTop: `1px solid ${C.border}`,
                  background: '#FAFAFA',
                }}>
                  <span style={{ fontSize: 12, color: C.grayLight }}>
                    {pagination.total} dossiers · Page {page} sur {pagination.pages}
                  </span>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={btnPageStyle}>← Préc.</button>
                    <button disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)} style={btnPageStyle}>Suiv. →</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ONGLET FOURNISSEURS ───────────────────────────────────────────── */}
      {onglet === 'fournisseurs' && (
        <OngletFournisseurs
          fournisseurs={fournisseurs}
          onReload={chargerFournisseurs}
          showForm={showNouveauFournisseur}
          setShowForm={setShowNouveauFournisseur}
        />
      )}

      {/* ── ONGLET SEUILS ─────────────────────────────────────────────────── */}
      {onglet === 'seuils' && <OngletSeuils abattoirs={abattoirs} monRole={monRole} />}

      {/* ── MODALS ────────────────────────────────────────────────────────── */}
      {showNouveauDossier && (
        <ModalNouveauDossier
          abattoirs={abattoirs}
          servicesRH={servicesRH}
          onClose={() => setShowNouveauDossier(false)}
          onSuccess={() => { setShowNouveauDossier(false); chargerDonnees(); }}
        />
      )}
      {showDetailDossier && (
        <ModalDetailDossier
          dossierId={showDetailDossier}
          onClose={() => { setShowDetailDossier(null); chargerDonnees(); }}
        />
      )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ONGLET FOURNISSEURS
// ─────────────────────────────────────────────────────────────────────────────
function OngletFournisseurs({ fournisseurs, onReload, showForm, setShowForm }) {
  const [form, setForm] = useState({ nom: '', ninea: '', adresse: '', telephone: '', email: '', type_fournisseur: 'GENERAL' });
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  const handleSubmit = async () => {
    if (!form.nom) { setErreur('Le nom est obligatoire'); return; }
    setSaving(true); setErreur('');
    try {
      await api.post('/achats/fournisseurs', form);
      setShowForm(false);
      setForm({ nom: '', ninea: '', adresse: '', telephone: '', email: '', type_fournisseur: 'GENERAL' });
      onReload();
    } catch (e) {
      setErreur(e.response?.data?.message || 'Erreur création');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <button onClick={() => setShowForm(true)}
          style={{ background: C.primary, color: C.white, border: 'none',
            padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14 }}>
          + Nouveau Fournisseur
        </button>
      </div>

      {showForm && (
        <div style={{ background: C.white, borderRadius: 10, padding: 24,
          boxShadow: '0 2px 12px #0002', marginBottom: 20 }}>
          <h3 style={{ margin: '0 0 16px', color: C.primary }}>Nouveau Fournisseur</h3>
          {erreur && <div style={{ color: C.danger, marginBottom: 12, fontSize: 14 }}>{erreur}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[
              { key: 'nom', label: 'Nom *', placeholder: 'Raison sociale' },
              { key: 'ninea', label: 'NINEA', placeholder: 'Numéro NINEA' },
              { key: 'telephone', label: 'Téléphone', placeholder: '+221 XX XXX XX XX' },
              { key: 'email', label: 'Email', placeholder: 'contact@fournisseur.sn' },
              { key: 'adresse', label: 'Adresse', placeholder: 'Adresse complète' },
            ].map(f => (
              <div key={f.key}>
                <label style={labelStyle}>{f.label}</label>
                <input value={form[f.key] || ''} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder} style={inputStyle} />
              </div>
            ))}
            <div>
              <label style={labelStyle}>Type</label>
              <select value={form.type_fournisseur} onChange={e => setForm({ ...form, type_fournisseur: e.target.value })}
                style={inputStyle}>
                <option value="GENERAL">Général</option>
                <option value="SERVICES">Services</option>
                <option value="TRAVAUX">Travaux</option>
                <option value="MATERIEL">Matériel</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end' }}>
            <button onClick={() => setShowForm(false)}
              style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.border}`,
                background: C.white, cursor: 'pointer', fontSize: 14 }}>Annuler</button>
            <button onClick={handleSubmit} disabled={saving}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none',
                background: C.primary, color: C.white, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: C.white, borderRadius: 10, boxShadow: '0 1px 4px #0001', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: C.primary }}>
              {['Nom', 'NINEA', 'Type', 'Téléphone', 'Email', 'Statut'].map(h => (
                <th key={h} style={{ color: C.white, padding: '12px 14px', textAlign: 'left', fontSize: 12, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {fournisseurs.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: 'center', padding: 30, color: C.gray }}>
                Aucun fournisseur enregistré
              </td></tr>
            ) : fournisseurs.map((f, i) => (
              <tr key={f.id} style={{ background: i % 2 === 0 ? C.white : '#f8fafc',
                borderBottom: `1px solid ${C.border}` }}>
                <td style={{ ...tdStyle, fontWeight: 600 }}>{f.nom}</td>
                <td style={tdStyle}>{f.ninea || '—'}</td>
                <td style={tdStyle}>{f.type_fournisseur}</td>
                <td style={tdStyle}>{f.telephone || '—'}</td>
                <td style={tdStyle}>{f.email || '—'}</td>
                <td style={tdStyle}>
                  <span style={{ color: f.actif ? C.success : C.danger, fontWeight: 600, fontSize: 12 }}>
                    {f.actif ? '● Actif' : '○ Inactif'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ONGLET SEUILS
// ─────────────────────────────────────────────────────────────────────────────
function OngletSeuils({ abattoirs, monRole }) {
  const peutGerer = ['admin', 'super_admin'].includes(monRole);
  const FORM_VIDE = {
    nom: '', montant_min: '', montant_max: '',
    nb_proformats_requis: '3', type_dossier: 'TOUS', abattoir_id: '',
    peut_contourner_urgent: false, validation_direction_requise: false,
  };
  const [seuils, setSeuils] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // null = nouveau, objet = modifier
  const [form, setForm] = useState(FORM_VIDE);
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');
  const [simMontant, setSimMontant] = useState('');

  const charger = useCallback(async () => {
    try {
      const res = await api.get('/achats/seuils');
      setSeuils(res.data || []);
    } catch {}
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const ouvrirNouveau = () => {
    setEditTarget(null);
    setForm(FORM_VIDE);
    setErreur('');
    setShowModal(true);
  };

  const ouvrirModifier = (s) => {
    setEditTarget(s);
    setForm({
      nom: s.nom, montant_min: s.montant_min, montant_max: s.montant_max || '',
      nb_proformats_requis: String(s.nb_proformats_requis),
      type_dossier: s.type_dossier || 'TOUS',
      abattoir_id: s.abattoir_id || '',
      peut_contourner_urgent: s.peut_contourner_urgent || false,
      validation_direction_requise: s.validation_direction_requise || false,
    });
    setErreur('');
    setShowModal(true);
  };

  const handleSubmit = async () => {
    if (!form.nom || form.montant_min === '') {
      setErreur('Nom et montant minimum sont obligatoires');
      return;
    }
    setSaving(true); setErreur('');
    const payload = {
      ...form,
      montant_min: Number(form.montant_min),
      montant_max: form.montant_max !== '' ? Number(form.montant_max) : null,
      nb_proformats_requis: Number(form.nb_proformats_requis),
      abattoir_id: form.abattoir_id || null,
    };
    try {
      if (editTarget) {
        await api.put(`/achats/seuils/${editTarget.id}`, payload);
      } else {
        await api.post('/achats/seuils', payload);
      }
      setShowModal(false);
      charger();
    } catch (e) {
      setErreur(e.response?.data?.message || 'Erreur');
    } finally { setSaving(false); }
  };

  const supprimerSeuil = async (id) => {
    if (!window.confirm('Désactiver ce seuil ?')) return;
    try { await api.delete(`/achats/seuils/${id}`); charger(); } catch {}
  };

  // Simulateur côté client
  const seuilSimule = simMontant
    ? seuils
        .filter(s => Number(simMontant) >= s.montant_min &&
          (s.montant_max === null || Number(simMontant) <= s.montant_max))
        .sort((a, b) => b.montant_min - a.montant_min)[0] || null
    : null;

  const thS = { color: C.white, padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4 };
  const tdS = { padding: '10px 14px', fontSize: 13, color: C.textDark, borderBottom: `1px solid ${C.border}` };
  const Toggle = ({ val, onChange }) => (
    <div onClick={onChange} style={{
      width: 40, height: 22, borderRadius: 11, cursor: 'pointer',
      background: val ? C.primary : '#ccc', position: 'relative', transition: '0.2s',
    }}>
      <div style={{
        position: 'absolute', top: 3, left: val ? 20 : 3,
        width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: '0.2s',
      }} />
    </div>
  );

  return (
    <div>
      {/* En-tête */}
      {peutGerer && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <button onClick={ouvrirNouveau}
            style={{ background: C.primary, color: C.white, border: 'none',
              padding: '9px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            + Nouveau seuil
          </button>
        </div>
      )}

      {/* Tableau */}
      <div style={{ background: C.white, borderRadius: 10, border: `0.5px solid ${C.border}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', overflow: 'hidden', marginBottom: 20 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: C.primary }}>
              {['Tranche', 'Montant min', 'Montant max', 'Proformats', 'Urgent?', 'Direction?', ...(peutGerer ? [''] : [])].map(h => (
                <th key={h} style={thS}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {seuils.length === 0 ? (
              <tr><td colSpan={peutGerer ? 7 : 6} style={{ textAlign: 'center', padding: 30, color: C.gray }}>
                Aucun seuil configuré
              </td></tr>
            ) : seuils.map((s, i) => (
              <tr key={s.id} style={{ background: i % 2 === 0 ? C.white : '#FAFAFA' }}>
                <td style={{ ...tdS, fontWeight: 600 }}>{s.nom}</td>
                <td style={tdS}>{formatMontant(s.montant_min)}</td>
                <td style={tdS}>{s.montant_max ? formatMontant(s.montant_max) : <span style={{ color: C.grayLight }}>Illimité</span>}</td>
                <td style={tdS}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: '50%', background: C.green100,
                    color: C.primary, fontWeight: 800, fontSize: 13 }}>
                    {s.nb_proformats_requis}
                  </span>
                </td>
                <td style={tdS}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                    background: s.peut_contourner_urgent ? C.green100 : '#F5F5F5',
                    color: s.peut_contourner_urgent ? C.primary : C.grayLight }}>
                    {s.peut_contourner_urgent ? 'Oui' : 'Non'}
                  </span>
                </td>
                <td style={tdS}>
                  <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                    background: s.validation_direction_requise ? '#FFF3E0' : '#F5F5F5',
                    color: s.validation_direction_requise ? C.orange : C.grayLight }}>
                    {s.validation_direction_requise ? 'Requise' : 'Non'}
                  </span>
                </td>
                {peutGerer && (
                  <td style={{ ...tdS, whiteSpace: 'nowrap' }}>
                    <button onClick={() => ouvrirModifier(s)}
                      style={{ background: C.green100, color: C.primary, border: 'none',
                        padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12, marginRight: 6 }}>
                      Modifier
                    </button>
                    <button onClick={() => supprimerSeuil(s.id)}
                      style={{ background: '#fde8e8', color: C.danger, border: 'none',
                        padding: '4px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                      Supprimer
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Simulateur */}
      <div style={{ background: C.white, borderRadius: 10, border: `0.5px solid ${C.border}`,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)', padding: '16px 20px' }}>
        <div style={{ fontWeight: 700, color: C.textDark, fontSize: 13, marginBottom: 12 }}>
          🧮 Simulateur de seuil
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: C.textMid }}>Pour un dossier de</span>
          <input
            type="number" placeholder="Montant en FCFA"
            value={simMontant} onChange={e => setSimMontant(e.target.value)}
            style={{ ...inputStyle, width: 200, margin: 0 }}
          />
          {simMontant && (
            seuilSimule ? (
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ padding: '4px 12px', borderRadius: 20, background: C.green100,
                  color: C.primary, fontWeight: 700, fontSize: 12 }}>
                  {seuilSimule.nom}
                </span>
                <span style={{ fontSize: 13, color: C.textMid }}>
                  → <strong>{seuilSimule.nb_proformats_requis}</strong> proformat(s) requis
                </span>
                <span style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                  background: seuilSimule.validation_direction_requise ? '#FFF3E0' : C.green100,
                  color: seuilSimule.validation_direction_requise ? C.orange : C.primary }}>
                  Direction : {seuilSimule.validation_direction_requise ? 'Requise' : 'Non requise'}
                </span>
              </div>
            ) : (
              <span style={{ fontSize: 13, color: C.danger }}>Aucun seuil correspondant</span>
            )
          )}
        </div>
      </div>

      {/* Modal nouveau/modifier */}
      {showModal && (
        <div style={overlayStyle}>
          <div style={{ ...modalStyle, maxWidth: 560 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, color: C.primary, fontSize: 18 }}>
                {editTarget ? `Modifier : ${editTarget.nom}` : 'Nouveau seuil'}
              </h2>
              <button onClick={() => setShowModal(false)} style={btnCloseStyle}>✕</button>
            </div>
            {erreur && <div style={{ color: C.danger, background: '#fde8e8', padding: '10px 14px',
              borderRadius: 8, marginBottom: 14, fontSize: 13 }}>{erreur}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Nom du seuil *</label>
                <input value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })}
                  placeholder="Ex : Achat moyen" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Montant minimum (FCFA) *</label>
                <input type="number" value={form.montant_min}
                  onChange={e => setForm({ ...form, montant_min: e.target.value })}
                  placeholder="0" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Montant maximum (FCFA)</label>
                <input type="number" value={form.montant_max}
                  onChange={e => setForm({ ...form, montant_max: e.target.value })}
                  placeholder="Vide = illimité" style={inputStyle} />
              </div>
              <div>
                <label style={labelStyle}>Proformats requis</label>
                <select value={form.nb_proformats_requis}
                  onChange={e => setForm({ ...form, nb_proformats_requis: e.target.value })} style={inputStyle}>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Type de dossier</label>
                <select value={form.type_dossier}
                  onChange={e => setForm({ ...form, type_dossier: e.target.value })} style={inputStyle}>
                  <option value="TOUS">Tous</option>
                  <option value="ACHAT">Achat</option>
                  <option value="TRAVAUX">Travaux</option>
                </select>
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>Abattoir spécifique</label>
                <select value={form.abattoir_id}
                  onChange={e => setForm({ ...form, abattoir_id: e.target.value })} style={inputStyle}>
                  <option value="">Tous les abattoirs</option>
                  {abattoirs.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ ...labelStyle, margin: 0 }}>Contournable si urgent</label>
                <Toggle val={form.peut_contourner_urgent}
                  onChange={() => setForm({ ...form, peut_contourner_urgent: !form.peut_contourner_urgent })} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ ...labelStyle, margin: 0 }}>Validation direction requise</label>
                <Toggle val={form.validation_direction_requise}
                  onChange={() => setForm({ ...form, validation_direction_requise: !form.validation_direction_requise })} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)}
                style={{ padding: '8px 18px', borderRadius: 8, border: `1px solid ${C.border}`,
                  background: C.white, cursor: 'pointer', fontSize: 13 }}>Annuler</button>
              <button onClick={handleSubmit} disabled={saving}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none',
                  background: C.primary, color: C.white, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL — NOUVEAU DOSSIER
// ─────────────────────────────────────────────────────────────────────────────
function ModalNouveauDossier({ abattoirs, servicesRH = [], onClose, onSuccess }) {
  const [services, setServices] = useState([]);
  const [form, setForm] = useState({
    type_dossier: 'ACHAT', abattoir_id: '', service_demandeur_id: '',
    objet: '', description_besoins: '', montant_estime: '',
    urgence: false, justification_urgence: '', date_besoin_souhaitee: '',
  });
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');

  useEffect(() => {
    api.get('/achats/services').then(r => {
      setServices(r.data || []);
    }).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!form.abattoir_id || !form.objet || !form.montant_estime) {
      setErreur('Abattoir, objet et montant estimé sont obligatoires'); return;
    }
    if (!form.service_demandeur_id) {
      setErreur('Veuillez sélectionner un service demandeur'); return;
    }
    setSaving(true); setErreur('');
    try {
      await api.post('/achats/dossiers', { ...form, montant_estime: Number(form.montant_estime) });
      onSuccess();
    } catch (e) {
      setErreur(e.response?.data?.message || 'Erreur création dossier');
    } finally { setSaving(false); }
  };

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, maxWidth: 680 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ margin: 0, color: C.primary, fontSize: 20 }}>📁 Nouveau Dossier d'Achat</h2>
          <button onClick={onClose} style={btnCloseStyle}>✕</button>
        </div>

        {erreur && <div style={{ color: C.danger, background: '#fde8e8', padding: '10px 14px',
          borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{erreur}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div>
            <label style={labelStyle}>Type de dossier *</label>
            <select value={form.type_dossier} onChange={e => setForm({ ...form, type_dossier: e.target.value })} style={inputStyle}>
              <option value="ACHAT">Achat</option>
              <option value="TRAVAUX">Travaux</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Abattoir *</label>
            <select value={form.abattoir_id} onChange={e => setForm({ ...form, abattoir_id: e.target.value, service_demandeur_id: '' })} style={inputStyle}>
              <option value="">Sélectionner...</option>
              {abattoirs.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Service demandeur *</label>
            <select value={form.service_demandeur_id} onChange={e => setForm({ ...form, service_demandeur_id: e.target.value })} style={inputStyle}>
              <option value="">Sélectionner...</option>
              {(servicesRH.length > 0 ? servicesRH : services).map(s => <option key={s.id} value={s.id}>{s.nom}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Montant estimé (FCFA) *</label>
            <input type="number" value={form.montant_estime}
              onChange={e => setForm({ ...form, montant_estime: e.target.value })}
              placeholder="Ex: 500000" style={inputStyle} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Objet *</label>
            <input value={form.objet} onChange={e => setForm({ ...form, objet: e.target.value })}
              placeholder="Intitulé du dossier d'achat" style={inputStyle} />
          </div>
          <div style={{ gridColumn: 'span 2' }}>
            <label style={labelStyle}>Description des besoins</label>
            <textarea value={form.description_besoins}
              onChange={e => setForm({ ...form, description_besoins: e.target.value })}
              rows={3} placeholder="Description détaillée des besoins..."
              style={{ ...inputStyle, resize: 'vertical' }} />
          </div>
          <div>
            <label style={labelStyle}>Date de besoin souhaitée</label>
            <input type="date" value={form.date_besoin_souhaitee}
              onChange={e => setForm({ ...form, date_besoin_souhaitee: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 24 }}>
            <input type="checkbox" id="urgence" checked={form.urgence}
              onChange={e => setForm({ ...form, urgence: e.target.checked })}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <label htmlFor="urgence" style={{ fontSize: 14, color: C.danger, fontWeight: 600, cursor: 'pointer' }}>
              ⚡ Cas d'urgence
            </label>
          </div>
          {form.urgence && (
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>Justification de l'urgence *</label>
              <textarea value={form.justification_urgence}
                onChange={e => setForm({ ...form, justification_urgence: e.target.value })}
                rows={2} placeholder="Expliquer pourquoi ce dossier est urgent..."
                style={{ ...inputStyle, resize: 'vertical' }} />
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 24, justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '10px 20px', borderRadius: 8, border: `1px solid ${C.border}`,
              background: C.white, cursor: 'pointer', fontSize: 14 }}>Annuler</button>
          <button onClick={handleSubmit} disabled={saving}
            style={{ padding: '10px 24px', borderRadius: 8, border: 'none',
              background: C.primary, color: C.white, cursor: 'pointer',
              fontSize: 14, fontWeight: 600, opacity: saving ? 0.7 : 1 }}>
            {saving ? 'Création...' : '✓ Créer le Dossier'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL — DÉTAIL DOSSIER (workflow + proformats + historique)
// ─────────────────────────────────────────────────────────────────────────────
function ModalDetailDossier({ dossierId, onClose }) {
  const { utilisateur: moi } = useAuth();
  const monRole = moi?.role || '';
  const [dossier, setDossier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onglet, setOnglet] = useState('info');
  const [transition, setTransition] = useState({ nouveau_statut: '', commentaire: '' });
  const [bordereauFichier, setBordereauFichier] = useState(null);
  const [saving, setSaving] = useState(false);
  const [erreur, setErreur] = useState('');
  const [succes, setSucces] = useState('');
  const [fournisseurs, setFournisseurs] = useState([]);
  const [lignes, setLignes] = useState([]);
  const [ligneEdit, setLigneEdit] = useState(null); // id de la ligne en cours d'édition
  const [ligneForm, setLigneForm] = useState({});
  const [showFormLigne, setShowFormLigne] = useState(false);
  const [nouvelleLigne, setNouvelleLigne] = useState({ designation: '', quantite: 1, unite: 'unité', prix_unitaire_estime: 0, observations: '' });

  // Form proformat
  const [showFormProformat, setShowFormProformat] = useState(false);
  const [formProformat, setFormProformat] = useState({
    fournisseur_id: '', numero_proformat: '', date_proformat: '',
    montant_ht: '', tva: 18, montant_ttc: '', delai_livraison: '', observations: '',
  });

  const charger = useCallback(async () => {
    try {
      const [dRes, fRes, lRes] = await Promise.all([
        api.get(`/achats/dossiers/${dossierId}`),
        api.get('/achats/fournisseurs'),
        api.get(`/achats/dossiers/${dossierId}/lignes`).catch(() => ({ data: [] })),
      ]);
      setDossier(dRes.data);
      setFournisseurs(fRes.data || []);
      setLignes(lRes.data || []);
    } catch { } finally { setLoading(false); }
  }, [dossierId]);

  useEffect(() => { charger(); }, [charger]);

  const handleTransition = async () => {
    if (!transition.nouveau_statut) { setErreur('Choisir un statut cible'); return; }
    setSaving(true); setErreur(''); setSucces('');
    try {
      let payload;
    if (transition.nouveau_statut === 'LIVRE' && bordereauFichier) {
      payload = new FormData();
      payload.append('nouveau_statut', transition.nouveau_statut);
      payload.append('commentaire', transition.commentaire || '');
      payload.append('bordereau', bordereauFichier);
    } else {
      payload = transition;
    }
    const res = await api.post(`/achats/dossiers/${dossierId}/transition`, payload);
      setSucces(res.data.message);
      setTransition({ nouveau_statut: '', commentaire: '' });
      setBordereauFichier(null);
      charger();
    } catch (e) {
      setErreur(e.response?.data?.message || 'Erreur transition');
    } finally { setSaving(false); }
  };

  const handleAjouterProformat = async () => {
    if (!formProformat.fournisseur_id || !formProformat.numero_proformat || !formProformat.montant_ttc) {
      setErreur('Fournisseur, numéro et montant TTC sont obligatoires'); return;
    }
    setSaving(true); setErreur('');
    try {
      const fd = new FormData();
        Object.entries(formProformat).forEach(([k, v]) => { if (k !== 'fichier' && v !== '' && v !== null) fd.append(k, v); });
        fd.append('fichier', formProformat.fichier);
        await api.post(`/achats/dossiers/${dossierId}/proformats`, fd);
      setShowFormProformat(false);
      setFormProformat({ fournisseur_id: '', numero_proformat: '', date_proformat: '',
        montant_ht: '', tva: 18, montant_ttc: '', delai_livraison: '', observations: '' });
      charger();
    } catch (e) {
      setErreur(e.response?.data?.message || 'Erreur ajout proformat');
    } finally { setSaving(false); }
  };

  const selectionnerProformat = async (pfId) => {
    const motif = window.prompt('Motif de sélection de ce proformat :');
    if (motif === null) return;
    try {
      await api.post(`/achats/proformats/${pfId}/selectionner`, { motif_selection: motif });
      charger();
    } catch (e) { alert(e.response?.data?.message || 'Erreur'); }
  };

  // Transitions disponibles selon statut actuel
  const TRANSITIONS_DISPO = {
    BROUILLON:       ['SOUMIS'],
    SOUMIS:          ['EN_MAINTENANCE', 'EN_ACHATS', 'REJETE'],
    EN_MAINTENANCE:  ['EN_ACHATS', 'REJETE'],
    EN_ACHATS:       ['EN_CONTROLE', 'REJETE'],
    EN_CONTROLE:     ['EN_COMPTABILITE', 'REJETE'],
    EN_COMPTABILITE: ['APPROUVE', 'REJETE'],
    APPROUVE:        ['EN_EXECUTION'],
    EN_EXECUTION:    ['LIVRE'],
    LIVRE:           ['FACTURE_RECUE'],
    FACTURE_RECUE:   ['PAYE'],
    PAYE:            ['CLOTURE'],
  };

  if (loading) return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, textAlign: 'center', padding: 60 }}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
        Chargement...
      </div>
    </div>
  );

  if (!dossier) return null;
  const transitionsDispo = TRANSITIONS_DISPO[dossier.statut] || [];

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, maxWidth: 860, maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
          marginBottom: 16, flexShrink: 0 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
              <span style={{ fontFamily: 'monospace', fontWeight: 700, color: C.primary, fontSize: 16 }}>
                {dossier.numero}
              </span>
              <Badge statut={dossier.statut} />
              {dossier.urgence && (
                <span style={{ background: '#fde8e8', color: C.danger, padding: '2px 8px',
                  borderRadius: 8, fontSize: 11, fontWeight: 700 }}>⚡ URGENT</span>
              )}
            </div>
            <h2 style={{ margin: 0, fontSize: 17, color: '#2c3e50', maxWidth: 600 }}>{dossier.objet}</h2>
          </div>
          <button onClick={onClose} style={btnCloseStyle}>✕</button>
        </div>

        {/* Messages */}
        {erreur && <div style={{ color: C.danger, background: '#fde8e8', padding: '8px 14px',
          borderRadius: 8, marginBottom: 12, fontSize: 13, flexShrink: 0 }}>{erreur}</div>}
        {succes && <div style={{ color: C.success, background: '#eafaf1', padding: '8px 14px',
          borderRadius: 8, marginBottom: 12, fontSize: 13, flexShrink: 0 }}>{succes}</div>}

        {/* Onglets */}
        <div style={{ display: 'flex', gap: 4, borderBottom: `2px solid ${C.border}`,
          marginBottom: 16, flexShrink: 0 }}>
          {[
            { id: 'info', label: 'ℹ️ Infos' },
            { id: 'lignes', label: `📦 Lignes (${lignes.length})` },
            { id: 'proformats', label: `📄 Proformats (${dossier.proformats?.length || 0})` },
            { id: 'workflow', label: '🔄 Workflow' },
            { id: 'historique', label: `📋 Historique (${dossier.historique?.length || 0})` },
          ].filter(t => {
            if (monRole === 'prepose' && dossier.statut !== 'BROUILLON') {
              return t.id === 'info' || t.id === 'historique';
            }
            if (monRole === 'prepose' && dossier.statut === 'BROUILLON') {
              return t.id === 'info' || t.id === 'lignes' || t.id === 'historique';
            }
            return true;
          }).map(t => (
            <button key={t.id} onClick={() => setOnglet(t.id)}
              style={{ border: 'none', background: 'none', padding: '8px 16px', cursor: 'pointer',
                fontSize: 13, fontWeight: 600, color: onglet === t.id ? C.primary : C.gray,
                borderBottom: onglet === t.id ? `2px solid ${C.primary}` : '2px solid transparent',
                marginBottom: -2 }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Contenu scrollable */}
        <div style={{ overflowY: 'auto', flex: 1 }}>

          {/* ── Infos ────────────────────────────────────────────────── */}
          {onglet === 'info' && (
            <div>
            {monRole === 'prepose' && dossier.statut !== 'BROUILLON' && (
              <div style={{ background: '#eaf4fb', border: '1px solid #aed6f1', borderRadius: 8,
                padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#1a5276' }}>
                📋 Dossier soumis — suivi en lecture seule
              </div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {[
                { label: 'Type', value: dossier.type_dossier },
                { label: 'Abattoir', value: dossier.abattoir_nom },
                { label: 'Service demandeur', value: dossier.service_nom },
                { label: 'Demandeur', value: dossier.demandeur_nom },
                { label: 'Montant estimé', value: formatMontant(dossier.montant_estime) },
                { label: 'Montant retenu', value: dossier.montant_retenu ? formatMontant(dossier.montant_retenu) : '—' },
                { label: 'Date souhaitée', value: dossier.date_besoin_souhaitee ? new Date(dossier.date_besoin_souhaitee).toLocaleDateString('fr-FR') : '—' },
                { label: 'Date création', value: new Date(dossier.created_at).toLocaleDateString('fr-FR') },
              ].map(f => (
                <div key={f.label} style={{ borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                  <div style={{ fontSize: 12, color: C.gray, marginBottom: 2 }}>{f.label}</div>
                  <div style={{ fontWeight: 600, color: '#2c3e50' }}>{f.value || '—'}</div>
                </div>
              ))}
              {dossier.description_besoins && (
                <div style={{ gridColumn: 'span 2', borderBottom: `1px solid ${C.border}`, paddingBottom: 10 }}>
                  <div style={{ fontSize: 12, color: C.gray, marginBottom: 4 }}>Description des besoins</div>
                  <div style={{ fontSize: 14, color: '#2c3e50', lineHeight: 1.6 }}>{dossier.description_besoins}</div>
                </div>
              )}
            </div>
            </div>
          )}


          {/* ── Lignes de dossier ─────────────────────────────────── */}
          {onglet === 'lignes' && (
            <div>
              {/* Bouton ajouter */}
              {dossier.statut === 'BROUILLON' && ['prepose', 'responsable_preposes', 'service_achats', 'responsable_achats', 'admin', 'super_admin'].includes(monRole) && !showFormLigne && (
                <button
                  onClick={() => setShowFormLigne(true)}
                  style={{ background: C.success, color: C.white, border: 'none', padding: '8px 16px',
                    borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 14 }}>
                  + Ajouter une ligne
                </button>
              )}

              {/* Formulaire nouvelle ligne */}
              {showFormLigne && (
                <div style={{ background: '#f0f9f4', border: `1px solid ${C.success}`, borderRadius: 8,
                  padding: 14, marginBottom: 16, display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr 2fr auto', gap: 8, alignItems: 'end' }}>
                  <div>
                    <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>Désignation *</div>
                    <input value={nouvelleLigne.designation} onChange={e => setNouvelleLigne({...nouvelleLigne, designation: e.target.value})}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                      placeholder="Article / prestation..." />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>Qté</div>
                    <input type="number" min="0" step="0.01" value={nouvelleLigne.quantite}
                      onChange={e => setNouvelleLigne({...nouvelleLigne, quantite: e.target.value})}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>Unité</div>
                    <input value={nouvelleLigne.unite} onChange={e => setNouvelleLigne({...nouvelleLigne, unite: e.target.value})}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>Prix unit. (FCFA)</div>
                    <input type="number" min="0" step="1" value={nouvelleLigne.prix_unitaire_estime}
                      onChange={e => setNouvelleLigne({...nouvelleLigne, prix_unitaire_estime: e.target.value})}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 11, color: C.gray, marginBottom: 3 }}>Observations</div>
                    <input value={nouvelleLigne.observations} onChange={e => setNouvelleLigne({...nouvelleLigne, observations: e.target.value})}
                      style={{ width: '100%', padding: '6px 8px', border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }}
                      placeholder="Optionnel..." />
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button onClick={async () => {
                      if (!nouvelleLigne.designation.trim()) return;
                      try {
                        await api.post(`/achats/dossiers/${dossierId}/lignes`, nouvelleLigne);
                        setNouvelleLigne({ designation: '', quantite: 1, unite: 'unité', prix_unitaire_estime: 0, observations: '' });
                        setShowFormLigne(false);
                        const [lRes, dRes] = await Promise.all([
                          api.get(`/achats/dossiers/${dossierId}/lignes`),
                          api.get(`/achats/dossiers/${dossierId}`),
                        ]);
                        setLignes(lRes.data || []);
                        setDossier(dRes.data);
                      } catch (e) { setErreur(e.response?.data?.message || 'Erreur'); }
                    }} style={{ background: C.success, color: C.white, border: 'none', padding: '7px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>✓</button>
                    <button onClick={() => setShowFormLigne(false)}
                      style={{ background: C.gray, color: C.white, border: 'none', padding: '7px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}>✕</button>
                  </div>
                </div>
              )}

              {/* Tableau des lignes */}
              {lignes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: C.gray }}>Aucune ligne — cliquez "+ Ajouter une ligne"</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: C.primary }}>
                      {['Désignation', 'Qté', 'Unité', 'Prix unitaire', 'Total estimé', 'Obs.', 'Actions'].map(h => (
                        <th key={h} style={{ color: C.white, padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 12 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {lignes.map((l, i) => ligneEdit === l.id ? (
                      <tr key={l.id} style={{ background: '#f0f9f4' }}>
                        <td style={{ padding: '6px 8px' }}><input value={ligneForm.designation || ''} onChange={e => setLigneForm({...ligneForm, designation: e.target.value})} style={{ width: '100%', padding: '4px 6px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} /></td>
                        <td style={{ padding: '6px 8px' }}><input type="number" min="0" step="0.01" value={ligneForm.quantite || ''} onChange={e => setLigneForm({...ligneForm, quantite: e.target.value})} style={{ width: 70, padding: '4px 6px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12 }} /></td>
                        <td style={{ padding: '6px 8px' }}><input value={ligneForm.unite || ''} onChange={e => setLigneForm({...ligneForm, unite: e.target.value})} style={{ width: 70, padding: '4px 6px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12 }} /></td>
                        <td style={{ padding: '6px 8px' }}><input type="number" min="0" step="1" value={ligneForm.prix_unitaire_estime || ''} onChange={e => setLigneForm({...ligneForm, prix_unitaire_estime: e.target.value})} style={{ width: 100, padding: '4px 6px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12 }} /></td>
                        <td style={{ padding: '6px 8px', color: C.gray, fontSize: 12 }}>{formatMontant(parseFloat(ligneForm.quantite||0) * parseFloat(ligneForm.prix_unitaire_estime||0))}</td>
                        <td style={{ padding: '6px 8px' }}><input value={ligneForm.observations || ''} onChange={e => setLigneForm({...ligneForm, observations: e.target.value})} style={{ width: '100%', padding: '4px 6px', border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 12, boxSizing: 'border-box' }} /></td>
                        <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                          <button onClick={async () => {
                            try {
                              await api.put(`/achats/dossiers/${dossierId}/lignes/${l.id}`, ligneForm);
                              const [lRes, dRes] = await Promise.all([
                                api.get(`/achats/dossiers/${dossierId}/lignes`),
                                api.get(`/achats/dossiers/${dossierId}`),
                              ]);
                              setLignes(lRes.data || []); setDossier(dRes.data); setLigneEdit(null);
                            } catch (e) { setErreur(e.response?.data?.message || 'Erreur'); }
                          }} style={{ background: C.success, color: C.white, border: 'none', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>✓</button>
                          <button onClick={() => setLigneEdit(null)} style={{ background: C.gray, color: C.white, border: 'none', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>✕</button>
                        </td>
                      </tr>
                    ) : (
                      <tr key={l.id} style={{ background: i % 2 === 0 ? C.white : '#f9f9f9', borderBottom: `1px solid ${C.border}` }}>
                        <td style={{ padding: '8px 10px', fontWeight: 500 }}>{l.designation}</td>
                        <td style={{ padding: '8px 10px' }}>{l.quantite}</td>
                        <td style={{ padding: '8px 10px', color: C.gray }}>{l.unite}</td>
                        <td style={{ padding: '8px 10px' }}>{formatMontant(l.prix_unitaire_estime)}</td>
                        <td style={{ padding: '8px 10px', fontWeight: 600, color: C.primary }}>{formatMontant(l.total_estime)}</td>
                        <td style={{ padding: '8px 10px', color: C.gray, fontSize: 12 }}>{l.observations || '—'}</td>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                          {dossier.statut === 'BROUILLON' && ['prepose', 'responsable_preposes', 'service_achats', 'responsable_achats', 'admin', 'super_admin'].includes(monRole) && (
                            <>
                              <button onClick={() => { setLigneEdit(l.id); setLigneForm({...l}); }}
                                style={{ background: C.secondary, color: C.white, border: 'none', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4 }}>✏️</button>
                              <button onClick={async () => {
                                if (!window.confirm('Supprimer cette ligne ?')) return;
                                try {
                                  await api.delete(`/achats/dossiers/${dossierId}/lignes/${l.id}`);
                                  const [lRes, dRes] = await Promise.all([
                                    api.get(`/achats/dossiers/${dossierId}/lignes`),
                                    api.get(`/achats/dossiers/${dossierId}`),
                                  ]);
                                  setLignes(lRes.data || []); setDossier(dRes.data);
                                } catch (e) { setErreur(e.response?.data?.message || 'Erreur'); }
                              }} style={{ background: C.danger, color: C.white, border: 'none', padding: '4px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}>🗑</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#eaf4fb', borderTop: `2px solid ${C.primary}` }}>
                      <td colSpan={4} style={{ padding: '10px 10px', fontWeight: 700, fontSize: 13 }}>TOTAL ESTIMÉ</td>
                      <td colSpan={3} style={{ padding: '10px 10px', fontWeight: 700, fontSize: 14, color: C.primary }}>
                        {formatMontant(lignes.reduce((s, l) => s + parseFloat(l.total_estime || 0), 0))} FCFA
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          )}

          {/* ── Proformats ────────────────────────────────────────────── */}
          {onglet === 'proformats' && (
            <div>
              {dossier.statut === 'EN_ACHATS' && (
                <div style={{ marginBottom: 16 }}>
                  <button onClick={() => setShowFormProformat(!showFormProformat)}
                    style={{ background: C.secondary, color: C.white, border: 'none',
                      padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                    {showFormProformat ? '✕ Annuler' : '+ Ajouter un Proformat'}
                  </button>
                </div>
              )}

              {showFormProformat && (
                <div style={{ background: C.light, borderRadius: 10, padding: 16, marginBottom: 16 }}>
                  <h4 style={{ margin: '0 0 12px', color: C.primary }}>Nouveau Proformat</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={labelStyle}>Fournisseur *</label>
                      <select value={formProformat.fournisseur_id}
                        onChange={e => setFormProformat({ ...formProformat, fournisseur_id: e.target.value })}
                        style={inputStyle}>
                        <option value="">Sélectionner...</option>
                        {fournisseurs.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
                      </select>
                    </div>
                    <div>
                      <label style={labelStyle}>N° Proformat *</label>
                      <input value={formProformat.numero_proformat}
                        onChange={e => setFormProformat({ ...formProformat, numero_proformat: e.target.value })}
                        placeholder="PRO-2024-001" style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Date</label>
                      <input type="date" value={formProformat.date_proformat}
                        onChange={e => setFormProformat({ ...formProformat, date_proformat: e.target.value })}
                        style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Montant HT</label>
                      <input type="number" value={formProformat.montant_ht}
                        onChange={e => setFormProformat({ ...formProformat, montant_ht: e.target.value })}
                        placeholder="0" style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>TVA (%)</label>
                      <input type="number" value={formProformat.tva}
                        onChange={e => setFormProformat({ ...formProformat, tva: e.target.value })}
                        style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Montant TTC *</label>
                      <input type="number" value={formProformat.montant_ttc}
                        onChange={e => setFormProformat({ ...formProformat, montant_ttc: e.target.value })}
                        placeholder="0" style={inputStyle} />
                    </div>
                    <div>
                      <label style={labelStyle}>Délai livraison</label>
                      <input value={formProformat.delai_livraison}
                        onChange={e => setFormProformat({ ...formProformat, delai_livraison: e.target.value })}
                        placeholder="Ex: 15 jours" style={inputStyle} />
                    </div>
                    <div style={{ gridColumn: 'span 2' }}>
                      <label style={labelStyle}>Observations</label>
                      <input value={formProformat.observations}
                        onChange={e => setFormProformat({ ...formProformat, observations: e.target.value })}
                        style={inputStyle} />
                    </div>
                  </div>
              <div style={{ gridColumn: 'span 3', marginBottom: 8 }}>
                <label style={labelStyle}>Fichier Proformat * <span style={{ fontSize: 11, color: '#888' }}>(PDF, JPG, PNG - max 10Mo)</span></label>
                <input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={e => setFormProformat({ ...formProformat, fichier: e.target.files[0] })}
                  style={{ display: 'block', width: '100%', padding: '6px', border: `1px solid ${C.border}`, borderRadius: 6, marginTop: 4, cursor: 'pointer' }}
                />
                {formProformat.fichier && (
                  <div style={{ fontSize: 12, color: '#27ae60', marginTop: 4 }}>
                    ✓ {formProformat.fichier.name}
                  </div>
                )}
              </div>
                  <div style={{ display: 'flex', gap: 10, marginTop: 12, justifyContent: 'flex-end' }}>
                    <button onClick={() => setShowFormProformat(false)}
                      style={{ padding: '7px 16px', borderRadius: 8, border: `1px solid ${C.border}`,
                        background: C.white, cursor: 'pointer', fontSize: 13 }}>Annuler</button>
                    <button onClick={handleAjouterProformat} disabled={saving}
                      style={{ padding: '7px 16px', borderRadius: 8, border: 'none',
                        background: C.secondary, color: C.white, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
                      Ajouter
                    </button>
                  </div>
                </div>
              )}

              {dossier.proformats?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: C.gray }}>
                  Aucun proformat enregistré
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {dossier.proformats.map(pf => (
                    <div key={pf.id} style={{
                      border: `2px solid ${pf.selectionne ? C.success : C.border}`,
                      borderRadius: 10, padding: 14,
                      background: pf.selectionne ? '#eafaf1' : C.white,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontWeight: 700, color: C.primary }}>
                            {pf.fournisseur_nom}
                            {pf.selectionne && <span style={{ color: C.success, marginLeft: 8, fontSize: 12 }}>✓ SÉLECTIONNÉ</span>}
                          </div>
                          <div style={{ fontSize: 12, color: C.gray }}>N° {pf.numero_proformat}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: 700, color: C.primary, fontSize: 16 }}>
                            {formatMontant(pf.montant_ttc)}
                          </div>
                          <div style={{ fontSize: 12, color: C.gray }}>TTC</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 20, marginTop: 8, fontSize: 13, color: C.gray }}>
                        {pf.montant_ht && <span>HT: {formatMontant(pf.montant_ht)}</span>}
                        {pf.tva && <span>TVA: {pf.tva}%</span>}
                        {pf.delai_livraison && <span>⏱ {pf.delai_livraison}</span>}
                      </div>
                      {!pf.selectionne && dossier.statut === 'EN_ACHATS' && (
                        <button onClick={() => selectionnerProformat(pf.id)}
                          style={{ marginTop: 10, background: C.success, color: C.white,
                            border: 'none', padding: '5px 14px', borderRadius: 6,
                            cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                          ✓ Sélectionner
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── Workflow ──────────────────────────────────────────────── */}
          {onglet === 'workflow' && (
            <div>
              {monRole === 'prepose' && dossier.statut !== 'BROUILLON' ? (
                <div style={{ textAlign: 'center', padding: 40 }}>
                  <div style={{ fontSize: 48, marginBottom: 16 }}>📋</div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8, color: '#2d7a4f' }}>
                    Dossier soumis avec succès
                  </div>
                  <div style={{ color: '#666', fontSize: 14, marginTop: 8 }}>
                    Ce dossier est en cours de traitement par le service concerné.
                    Votre rôle sur ce dossier est terminé.
                  </div>
                </div>
              ) : transitionsDispo.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: C.gray }}>
                  {dossier.statut === 'CLOTURE' ? '✅ Dossier clôturé' :
                   dossier.statut === 'REJETE' ? '❌ Dossier rejeté' :
                   dossier.statut === 'EN_ACHATS' && monRole === 'responsable_preposes' ? (
                     <div style={{ color: '#1a5276', background: '#eaf4fb', border: '1px solid #aed6f1',
                       borderRadius: 8, padding: '14px 20px', fontSize: 14 }}>
                       🛒 Ce dossier est traité par le service achats
                     </div>
                   ) :
                   'Aucune action disponible pour ce statut'}
                </div>
              ) : (
                <div>
                  <p style={{ color: C.gray, fontSize: 14, marginTop: 0 }}>
                    Statut actuel : <strong><Badge statut={dossier.statut} /></strong>
                  </p>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>Passer au statut *</label>
                    <select value={transition.nouveau_statut}
                      onChange={e => setTransition({ ...transition, nouveau_statut: e.target.value })}
                      style={inputStyle}>
                      <option value="">Choisir...</option>
                      {transitionsDispo.map(s => (
                        <option key={s} value={s}>{STATUTS_CONFIG[s]?.label || s}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: 16 }}>
                    <label style={labelStyle}>
                      Commentaire {transition.nouveau_statut === 'REJETE' ? '*' : '(optionnel)'}
                    </label>
                    <textarea value={transition.commentaire}
                      onChange={e => setTransition({ ...transition, commentaire: e.target.value })}
                      rows={3} placeholder="Commentaire sur cette transition..."
                      style={{ ...inputStyle, resize: 'vertical' }} />
                  </div>
                  {transition.nouveau_statut === 'LIVRE' && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 13 }}>
                    Bordereau de livraison * (PDF, JPG, PNG)
                  </label>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png"
                    onChange={e => setBordereauFichier(e.target.files[0])}
                    style={{ fontSize: 13 }} />
                  {bordereauFichier && <p style={{ color: 'green', fontSize: 12, marginTop: 4 }}>✓ {bordereauFichier.name}</p>}
                </div>
              )}
              <button onClick={handleTransition} disabled={saving || !transition.nouveau_statut || (transition.nouveau_statut === 'LIVRE' && !bordereauFichier)}
                    style={{
                      background: transition.nouveau_statut === 'REJETE' ? C.danger : C.primary,
                      color: C.white, border: 'none', padding: '10px 24px', borderRadius: 8,
                      cursor: 'pointer', fontSize: 14, fontWeight: 600,
                      opacity: saving || !transition.nouveau_statut ? 0.6 : 1,
                    }}>
                    {saving ? 'Traitement...' : `→ Valider la transition`}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Historique ────────────────────────────────────────────── */}
          {onglet === 'historique' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {dossier.historique?.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 30, color: C.gray }}>Aucun historique</div>
              ) : dossier.historique?.map((h, i) => (
                <div key={h.id} style={{
                  display: 'flex', gap: 14, alignItems: 'flex-start',
                  background: i === 0 ? '#eaf4fb' : C.white,
                  borderRadius: 8, padding: '10px 14px',
                  border: `1px solid ${C.border}`,
                }}>
                  <div style={{ fontSize: 20, flexShrink: 0 }}>
                    {h.statut_apres === 'REJETE' ? '❌' :
                     h.statut_apres === 'CLOTURE' ? '✅' :
                     h.statut_apres === 'APPROUVE' ? '🎯' : '→'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: '#2c3e50' }}>
                      {h.statut_avant ? <><Badge statut={h.statut_avant} /> → </> : 'Création → '}
                      <Badge statut={h.statut_apres} />
                    </div>
                    <div style={{ fontSize: 12, color: C.gray, marginTop: 4 }}>
                      <strong>{h.utilisateur_nom}</strong>
                      {' — '}
                      {new Date(h.created_at).toLocaleString('fr-FR')}
                    </div>
                    {h.commentaire && (
                      <div style={{ fontSize: 13, color: '#4a5568', marginTop: 6,
                        background: C.light, padding: '6px 10px', borderRadius: 6 }}>
                        💬 {h.commentaire}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Styles réutilisables ────────────────────────────────────────────────────
const tdStyle = { padding: '10px 14px', verticalAlign: 'middle', color: '#212121' };
const selectStyle = {
  padding: '7px 12px', borderRadius: 8, border: '1px solid #E0E0E0',
  fontSize: 13, background: '#fff', cursor: 'pointer', outline: 'none', color: '#212121',
};
const inputStyle = {
  width: '100%', padding: '9px 12px', borderRadius: 8,
  border: '1px solid #E0E0E0', fontSize: 13, outline: 'none',
  boxSizing: 'border-box', background: '#fff', color: '#212121',
  transition: 'border-color 0.15s ease',
};
const labelStyle = {
  display: 'block', fontSize: 12, fontWeight: 700,
  color: '#616161', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.5px',
};
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  zIndex: 1000, padding: 16,
};
const modalStyle = {
  background: '#fff', borderRadius: 12, padding: 28,
  width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
};
const btnCloseStyle = {
  background: '#F5F5F5', border: '1px solid #E0E0E0', borderRadius: 8, width: 32, height: 32,
  cursor: 'pointer', fontSize: 16, color: '#616161', flexShrink: 0,
};
const btnPageStyle = {
  background: '#fff', border: '1px solid #E0E0E0', borderRadius: 8,
  padding: '6px 16px', cursor: 'pointer', fontSize: 13, color: '#616161',
  transition: '0.15s ease',
};